import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Archive, Briefcase, CalendarPlus, ClipboardEdit, Contact,
  FileSearch, FileSignature, FileText, Gavel, ListTodo, Loader2, Mail, Pencil, Phone,
  Plus, Trash2, Check, PlusCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { mattersApi, practiceApi, clientsApi, errorDetailText } from '../services/api';
import { Client, Collaborator, Hearing, Matter, Task } from '../types';
import { useMattersStore } from '../store/mattersStore';
import { usePracticeStore } from '../store/practiceStore';
import { statusMeta, hearingStatusMeta } from '../lib/meta';
import { Badge, Button, Card, Skeleton, SkeletonCard } from '../components/ui';
import { CaseForm } from '../components/practice/CaseForm';
import { CollaboratorList } from '../components/practice/CollaboratorList';
import { PracticeAssistant } from '../components/practice/PracticeAssistant';
import { TaskRow } from '../components/practice/TaskRow';
import { dayLabel, fmtDay, waLink } from '../components/practice/format';
import { WhatsAppIcon } from './landing/data';

const INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent';

const SOURCE_LABELS: Record<string, string> = {
  assistant: 'via assistant',
  ecourts: 'eCourts',
};

const TASK_TYPES: { value: string; label: string }[] = [
  { value: 'drafting', label: 'Drafting' },
  { value: 'research', label: 'Research' },
  { value: 'enquiry', label: 'Enquiry' },
  { value: 'argument_prep', label: 'Argument prep' },
  { value: 'document_verification', label: 'Document verification' },
  { value: 'filing', label: 'Filing' },
  { value: 'other', label: 'Other' },
];

type TabId = 'overview' | 'hearings' | 'research' | 'drafts' | 'documents' | 'tasks' | 'people';

/**
 * The case file hub (`/app/cases/:id`, spec §4.1): header + tab row
 * (Overview | Hearings | Research | Drafts | Documents | Tasks | People),
 * case-scoped assistant docked below.
 */
export function MatterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Canonical URL is /app/cases/:id — legacy /app/matters/:id and
  // /app/practice/cases/:id redirect here (App.tsx), so back always goes to
  // the merged Cases list.
  const backTo = '/app/cases';
  const backLabel = 'Cases';

  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const setMatterDetail = useMattersStore((s) => s.setMatterDetail);
  const clearMatter = useMattersStore((s) => s.clearMatter);
  // Full detail (jobs + documents + drafts) from the per-matter cache — instant on revisit.
  const detail = useMattersStore((s) => (id ? s.detailById[id] : undefined));
  const listMatter = useMattersStore((s) => (id ? s.matters.find((m) => m.id === id) : undefined));
  const invalidatePractice = usePracticeStore((s) => s.invalidate);
  // Header-only seed: router state from the list link, else the list cache.
  const routeSeed = (location.state as { matter?: Matter } | null)?.matter;
  const header: Matter | undefined =
    detail ?? (routeSeed?.id === id ? routeSeed : undefined) ?? listMatter;
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editClient, setEditClient] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Local override so "Add court details" flips straight into the case form
  // without waiting for the cache to revalidate.
  const [trackedLocally, setTrackedLocally] = useState(false);
  const isCase = !!header?.case_status || trackedLocally;

  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // ---- practice state ----
  const [hearings, setHearings] = useState<Hearing[] | null>(null);
  const [caseTasks, setCaseTasks] = useState<Task[] | null>(null);
  const [collabs, setCollabs] = useState<Collaborator[]>([]);
  const [assistantScope, setAssistantScope] = useState<{ hearingId: string; opener: string } | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  // Add-hearing inline form
  const [showAddHearing, setShowAddHearing] = useState(false);
  const [newHearingDate, setNewHearingDate] = useState('');
  const [newHearingPurpose, setNewHearingPurpose] = useState('');
  const [isAddingHearing, setIsAddingHearing] = useState(false);

  const refreshDetail = useCallback(() => {
    if (!id) return;
    mattersApi
      .get(id)
      .then(({ data }) => setMatterDetail(data))
      .catch(() => {});
  }, [id, setMatterDetail]);

  const loadHearings = useCallback(() => {
    if (!id) return;
    practiceApi
      .listHearings(id)
      .then(({ data }) => setHearings(data))
      .catch(() => setHearings([]));
  }, [id]);

  const loadCaseTasks = useCallback(() => {
    if (!id) return;
    practiceApi
      .listTasks('mine')
      .then(({ data }) => setCaseTasks((data as Task[]).filter((t) => t.matter_id === id)))
      .catch(() => setCaseTasks([]));
  }, [id]);

  const loadCollabs = useCallback(() => {
    if (!id) return;
    practiceApi
      .listCollaborators(id)
      .then(({ data }) => setCollabs(data))
      .catch(() => setCollabs([]));
  }, [id]);

  // Always revalidate in the background; the seed/cache renders meanwhile.
  useEffect(() => {
    if (!id) return;
    let stale = false;
    mattersApi
      .get(id)
      .then(({ data }) => {
        if (!stale) setMatterDetail(data);
      })
      .catch((err: any) => {
        if (!stale) setError(errorDetailText(err, 'Failed to load the case'));
      });
    return () => {
      stale = true;
    };
  }, [id, setMatterDetail]);

  useEffect(() => {
    if (!isCase) return;
    loadHearings();
  }, [isCase, loadHearings]);

  useEffect(() => {
    loadCaseTasks();
    loadCollabs();
  }, [loadCaseTasks, loadCollabs]);

  const startEdit = () => {
    if (!header) return;
    setEditTitle(header.title);
    setEditClient(header.client_name || '');
    setEditNotes(header.description || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id || !editTitle.trim() || isSaving) return;
    setIsSaving(true);
    try {
      const { data } = await mattersApi.update(id, {
        title: editTitle.trim(),
        client_name: editClient.trim(),
        description: editNotes.trim(),
      });
      if (detail) setMatterDetail({ ...detail, ...data });
      setIsEditing(false);
      fetchMatters();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isDeleting) return;
    setIsDeleting(true);
    try {
      await mattersApi.remove(id);
      clearMatter(id);
      fetchMatters();
      invalidatePractice();
      navigate(backTo);
    } catch (err: any) {
      setShowDeleteDialog(false);
      if (
        err?.response?.status === 409 &&
        err.response.data?.detail?.code === 'matter_has_hearings'
      ) {
        // The diary guard — hearings are practice record; archive instead.
        setShowArchiveDialog(true);
      } else {
        setError(errorDetailText(err, 'Could not delete this case.'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async () => {
    if (!id || isArchiving) return;
    setIsArchiving(true);
    try {
      await practiceApi.updateCase(id, { case_status: 'archived' });
      setShowArchiveDialog(false);
      setShowDeleteDialog(false);
      refreshDetail();
      fetchMatters();
      invalidatePractice();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not archive the case.'));
      setShowArchiveDialog(false);
      setShowDeleteDialog(false);
    } finally {
      setIsArchiving(false);
    }
  };

  /** "Add court details to track hearings" — start tracking, then open the case form. */
  const handleAddCourtDetails = async () => {
    if (!id || isTracking) return;
    setIsTracking(true);
    setError('');
    try {
      const { data } = await practiceApi.updateCase(id, { case_status: 'active' });
      if (detail) setMatterDetail({ ...detail, ...data });
      setTrackedLocally(true);
      refreshDetail();
      invalidatePractice();
      setIsEditing(true); // straight into the case form for court details
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not start hearing tracking for this case.'));
    } finally {
      setIsTracking(false);
    }
  };

  const handleAddHearing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newHearingDate || isAddingHearing) return;
    setIsAddingHearing(true);
    try {
      await practiceApi.createHearing(id, {
        hearing_date: newHearingDate,
        purpose: newHearingPurpose.trim() || undefined,
      });
      setNewHearingDate('');
      setNewHearingPurpose('');
      setShowAddHearing(false);
      loadHearings();
      refreshDetail();
      invalidatePractice();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not add the hearing.'));
    } finally {
      setIsAddingHearing(false);
    }
  };

  const onLogOutcome = (h: Hearing) => {
    setAssistantScope({
      hearingId: h.id,
      opener: `Logging the outcome for the ${dayLabel(h.hearing_date)} hearing${
        h.purpose ? ` (${h.purpose})` : ''
      }. What happened?`,
    });
  };

  const goResearch = () => {
    if (!header || !id) return;
    const outcomes = (hearings ?? [])
      .filter((h) => h.outcome)
      .sort((a, b) => (a.hearing_date < b.hearing_date ? 1 : -1))
      .slice(0, 2);
    const courtLine = [header.court_name, header.case_number].filter(Boolean).join(' ');
    const prefill =
      `Case: ${header.title}.` +
      (courtLine ? ` ${courtLine}.` : '') +
      (outcomes.length
        ? ` Last hearing outcomes: ${outcomes
            .map((h) => `${fmtDay(h.hearing_date)}: ${h.outcome}`)
            .join('; ')}.`
        : '');
    navigate('/app/research/new', { state: { prefill, matterId: id } });
  };

  const goDraft = () => {
    if (!header || !id) return;
    navigate('/app/drafts/new', {
      state: {
        matterId: id,
        seedFields: {
          client_name: header.client_name || undefined,
          opposite_party: header.opposite_party || undefined,
          case_number: header.case_number || undefined,
          court_name: header.court_name || undefined,
        },
      },
    });
  };

  const sortedHearings = useMemo(
    () => (hearings ?? []).slice().sort((a, b) => (a.hearing_date < b.hearing_date ? 1 : -1)),
    [hearings]
  );
  const latestOutcome = sortedHearings.find((h) => h.outcome);
  const pendingOutcome = sortedHearings.find((h) => h.status === 'scheduled' && !h.outcome);

  /** Overview action: log the outcome of the pending hearing (assistant opener). */
  const overviewLogOutcome = () => {
    setActiveTab('hearings');
    if (pendingOutcome) onLogOutcome(pendingOutcome);
  };

  // Recent items across tabs for the Overview.
  const recentItems = useMemo(() => {
    if (!detail) return [];
    const items: { key: string; kind: string; title: string; created_at: string; to: string }[] = [
      ...detail.jobs.map((j) => ({
        key: `job-${j.id}`,
        kind: 'Research',
        title: j.title,
        created_at: j.created_at,
        to: `/app/research/${j.id}`,
      })),
      ...(detail.drafts ?? []).map((d) => ({
        key: `draft-${d.id}`,
        kind: 'Draft',
        title: d.title,
        created_at: d.created_at,
        to: `/app/drafts/${d.id}`,
      })),
      ...detail.documents.map((doc) => ({
        key: `doc-${doc.id}`,
        kind: 'Document',
        title: doc.original_filename,
        created_at: doc.created_at,
        to: `/app/documents/${doc.id}`,
      })),
    ];
    return items.sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 5);
  }, [detail]);

  // Full-page skeleton only when we have nothing at all (cold direct visit).
  if (!header && !error) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!header) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error || 'Case not found'}</p>
        </div>
        <Link to={backTo} className="mt-4 inline-flex items-center space-x-2 text-legal-500 hover:text-legal-700">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to {backLabel}</span>
        </Link>
      </div>
    );
  }

  const isSharedCase = !!header.shared_by;

  const TABS: { id: TabId; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'hearings', label: 'Hearings', show: isCase },
    { id: 'research', label: 'Research', show: true },
    { id: 'drafts', label: 'Drafts', show: true },
    { id: 'documents', label: 'Documents', show: true },
    { id: 'tasks', label: 'Tasks', show: true },
    { id: 'people', label: 'People', show: true },
  ];

  const metaLine = [
    header.court_name,
    header.case_number || header.cnr,
    header.case_stage,
    header.next_hearing_date ? `Next: ${dayLabel(header.next_hearing_date)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="p-2 sm:p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">{backLabel}</span>
      </button>

      {/* ---- Header ---- */}
      <div className="bg-white rounded-xl border border-legal-200 p-5 mb-5">
        {isEditing ? (
          isCase ? (
            <CaseForm
              caseId={id}
              initial={header}
              onSaved={() => {
                setIsEditing(false);
                refreshDetail();
                fetchMatters();
                invalidatePractice();
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <div className="space-y-3">
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={300}
                className="w-full text-lg font-semibold border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                value={editClient}
                onChange={(e) => setEditClient(e.target.value)}
                maxLength={255}
                placeholder="Client name"
                className={INPUT_CLS}
              />
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                maxLength={5000}
                rows={3}
                placeholder="Notes — court, next steps, anything worth remembering"
                className={`${INPUT_CLS} resize-none`}
              />
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSave}
                  disabled={!editTitle.trim() || isSaving}
                  className="flex items-center space-x-1.5 px-4 py-2 bg-accent text-legal-900 rounded-lg text-sm font-medium hover:bg-accent-400 transition-colors disabled:opacity-60"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span>Save</span>
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm text-legal-500 hover:text-legal-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 min-w-0">
                <div className="p-2.5 bg-legal-50 rounded-lg shrink-0">
                  {isCase ? (
                    <Gavel className="h-5 w-5 text-legal-500" />
                  ) : (
                    <Briefcase className="h-5 w-5 text-legal-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-legal-900 break-words">{header.title}</h1>
                  {metaLine && <p className="text-sm text-legal-600 mt-0.5 break-words">{metaLine}</p>}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {/* Client chip — links to the client page when a real Client exists */}
                    {header.client_id ? (
                      <Link
                        to={`/app/clients/${header.client_id}`}
                        data-track="case-client-chip"
                        className="inline-flex items-center gap-1 text-xs text-legal-600 bg-legal-50 border border-legal-200 rounded-full px-2.5 py-1 hover:border-accent hover:text-accent-700 transition-colors max-w-[14rem]"
                      >
                        <Contact className="h-3 w-3 shrink-0" />
                        <span className="truncate">{header.client_name || 'Client'}</span>
                      </Link>
                    ) : header.client_name ? (
                      <span className="inline-flex items-center gap-1 text-xs text-legal-500 bg-legal-50 border border-legal-200 rounded-full px-2.5 py-1 max-w-[14rem]">
                        <Contact className="h-3 w-3 shrink-0" />
                        <span className="truncate">{header.client_name}</span>
                      </span>
                    ) : null}
                    {header.case_status === 'active' && <Badge tone="success">Active case</Badge>}
                    {header.case_status === 'disposed' && <Badge tone="info">Disposed</Badge>}
                    {header.case_status === 'archived' && <Badge tone="neutral">Archived</Badge>}
                    {header.shared_by && <Badge tone="accent">Shared by {header.shared_by}</Badge>}
                  </div>
                  {header.description && (
                    <p className="text-sm text-legal-500 mt-2 whitespace-pre-wrap break-words">
                      {header.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1 shrink-0 ml-3">
                <button
                  onClick={startEdit}
                  className="p-2 text-legal-400 hover:text-legal-700 hover:bg-legal-50 rounded-lg transition-colors"
                  title="Edit case"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete case"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* "Add court details to track hearings" disclosure (replaces "Track as case") */}
            {!isCase && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  size="md"
                  data-track="case-add-court-details"
                  onClick={handleAddCourtDetails}
                  isLoading={isTracking}
                >
                  <Gavel className="h-4 w-4" /> Add court details to track hearings
                </Button>
                <p className="text-xs text-legal-400 mt-1.5">
                  Hearings, court details and tasks — the case shows up on your daily board.
                </p>
              </div>
            )}
          </>
        )}
        {error && !isEditing && <p className="text-sm text-red-600 mt-3">{error}</p>}
      </div>

      {/* ---- Tab row ---- */}
      <div
        data-tour="case-tabs"
        className="flex gap-1 overflow-x-auto border-b border-legal-200 mb-5"
        role="tablist"
        aria-label="Case file sections"
      >
        {TABS.filter((t) => t.show).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            data-track={`case-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={`shrink-0 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.id
                ? 'border-accent text-legal-900'
                : 'border-transparent text-legal-500 hover:text-legal-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ================= Overview ================= */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Action row */}
          <div data-tour="case-actions" className="flex flex-wrap gap-2">
            <Button variant="secondary" size="md" data-track="case-action-research" onClick={goResearch}>
              <FileSearch className="h-4 w-4" /> Research this issue
            </Button>
            <Button variant="secondary" size="md" data-track="case-action-draft" onClick={goDraft}>
              <FileSignature className="h-4 w-4" /> Draft for this case
            </Button>
            {isCase && (
              <Button
                variant="secondary"
                size="md"
                data-track="case-action-log-outcome"
                data-tour="log-outcome"
                onClick={overviewLogOutcome}
              >
                <ClipboardEdit className="h-4 w-4" /> Log outcome
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              data-track="case-action-add-task"
              onClick={() => setActiveTab('tasks')}
            >
              <ListTodo className="h-4 w-4" /> Add task
            </Button>
          </div>

          {/* Case meta grid */}
          {isCase && (
            <Card>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
                {(
                  [
                    ['Court', header.court_name],
                    ['Case number', header.case_number],
                    ['Stage', header.case_stage],
                    ['Appearing for', header.party_role],
                    ['Opposite party', header.opposite_party],
                    [
                      'Next hearing',
                      header.next_hearing_date
                        ? `${dayLabel(header.next_hearing_date)}${
                            header.next_hearing_purpose ? ` — ${header.next_hearing_purpose}` : ''
                          }`
                        : null,
                    ],
                  ] as [string, string | null | undefined][]
                ).map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-[11px] uppercase tracking-wide text-legal-400">{label}</dt>
                    <dd className="text-legal-800 mt-0.5 break-words">{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}

          {/* Latest outcome */}
          {isCase && latestOutcome && (
            <Card>
              <p className="text-[11px] uppercase tracking-wide text-legal-400 mb-1">
                Latest outcome — {dayLabel(latestOutcome.hearing_date)}
                {latestOutcome.purpose ? ` (${latestOutcome.purpose})` : ''}
              </p>
              <p className="text-sm text-legal-700 break-words">{latestOutcome.outcome}</p>
              {latestOutcome.next_date && (
                <p className="text-xs text-legal-400 mt-1">
                  Next date: {dayLabel(latestOutcome.next_date)}
                </p>
              )}
            </Card>
          )}

          {/* Recent items across tabs */}
          <div>
            <h2 className="font-semibold text-legal-900 mb-3">Recent in this case</h2>
            {!detail ? (
              <SkeletonCard lines={2} />
            ) : recentItems.length === 0 ? (
              <div className="bg-white rounded-card border border-legal-200 p-6 text-center text-sm text-legal-400">
                Nothing filed here yet — research, drafts and documents you create for this case
                land in the tabs above.
              </div>
            ) : (
              <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
                {recentItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    className="flex items-center justify-between gap-3 p-3.5 hover:bg-legal-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-legal-900 truncate">{item.title}</p>
                      <p className="text-xs text-legal-400 mt-0.5">
                        {item.kind} ·{' '}
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= Hearings ================= */}
      {activeTab === 'hearings' && isCase && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-legal-900">Hearings</h2>
            <button
              type="button"
              onClick={() => setShowAddHearing((v) => !v)}
              className="flex items-center space-x-1.5 text-sm text-accent-700 hover:text-accent-600 transition-colors"
            >
              <CalendarPlus className="h-4 w-4" />
              <span>Add hearing</span>
            </button>
          </div>

          {showAddHearing && (
            <form
              onSubmit={handleAddHearing}
              className="bg-white rounded-card border border-legal-200 p-4 mb-3 flex flex-col sm:flex-row gap-3 animate-slide-up"
            >
              <input
                type="date"
                value={newHearingDate}
                onChange={(e) => setNewHearingDate(e.target.value)}
                aria-label="Hearing date"
                className={`${INPUT_CLS} sm:w-44`}
              />
              <input
                value={newHearingPurpose}
                onChange={(e) => setNewHearingPurpose(e.target.value)}
                maxLength={255}
                placeholder="Purpose — e.g. Evidence"
                className={`${INPUT_CLS} flex-1`}
              />
              <Button type="submit" size="md" isLoading={isAddingHearing} disabled={!newHearingDate} className="shrink-0">
                <Plus className="h-4 w-4" /> Add
              </Button>
            </form>
          )}

          {hearings === null ? (
            <SkeletonCard lines={2} />
          ) : sortedHearings.length === 0 ? (
            <div className="bg-white rounded-card border border-legal-200 p-6 text-center text-sm text-legal-400">
              No hearings yet — add the next date and the board takes over.
            </div>
          ) : (
            <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
              {sortedHearings.map((h) => (
                <HearingTimelineRow
                  key={h.id}
                  hearing={h}
                  onLogOutcome={onLogOutcome}
                  onSaved={() => {
                    loadHearings();
                    refreshDetail();
                    invalidatePractice();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= Research ================= */}
      {activeTab === 'research' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-legal-900">Research</h2>
            <Link
              to="/app/research/new"
              state={{ matterId: header.id }}
              data-track="case-new-research"
              className="flex items-center space-x-1.5 text-sm text-accent-700 hover:text-accent-600 transition-colors"
            >
              <PlusCircle className="h-4 w-4" />
              <span>New research in this case</span>
            </Link>
          </div>
          {!detail ? (
            <SkeletonCard lines={2} />
          ) : detail.jobs.length === 0 ? (
            <div className="bg-white rounded-xl border border-legal-200 p-6 text-center text-sm text-legal-400">
              No research filed under this case yet.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-legal-200 divide-y divide-legal-100 stagger-children">
              {detail.jobs.map((job) => {
                const meta = statusMeta(job.status);
                const StatusIcon = meta.icon;
                return (
                  <Link
                    key={job.id}
                    to={`/app/research/${job.id}`}
                    className="flex items-center justify-between p-4 hover:bg-legal-50 transition-colors"
                  >
                    <div className="min-w-0 mr-3">
                      <p className="text-sm font-medium text-legal-900 truncate">{job.title}</p>
                      <p className="text-xs text-legal-400 mt-0.5">
                        {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                        {job.law_domain ? ` · ${job.law_domain}` : ''}
                      </p>
                    </div>
                    <span className={`flex items-center space-x-1 text-xs shrink-0 ${meta.text}`}>
                      <StatusIcon className="h-3.5 w-3.5" />
                      <span>{meta.label}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ================= Drafts ================= */}
      {activeTab === 'drafts' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-legal-900">Drafts</h2>
            <Link
              to="/app/drafts/new"
              state={{ matterId: header.id }}
              data-track="case-new-draft"
              className="flex items-center space-x-1.5 text-sm text-accent-700 hover:text-accent-600 transition-colors"
            >
              <PlusCircle className="h-4 w-4" />
              <span>New draft in this case</span>
            </Link>
          </div>
          {!detail ? (
            <SkeletonCard lines={1} />
          ) : !detail.drafts || detail.drafts.length === 0 ? (
            <div className="bg-white rounded-xl border border-legal-200 p-6 text-center text-sm text-legal-400">
              No drafts filed under this case yet.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-legal-200 divide-y divide-legal-100 stagger-children">
              {detail.drafts.map((d) => (
                <Link
                  key={d.id}
                  to={`/app/drafts/${d.id}`}
                  className="flex items-center justify-between p-4 hover:bg-legal-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 mr-3">
                    <FileSignature className="h-4 w-4 text-legal-400 shrink-0" />
                    <p className="text-sm font-medium text-legal-900 truncate">{d.title}</p>
                  </div>
                  <span className="text-xs text-legal-400 shrink-0">
                    {formatDistanceToNow(new Date(d.created_at), { addSuffix: true })}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= Documents ================= */}
      {activeTab === 'documents' && (
        <div>
          <h2 className="font-semibold text-legal-900 mb-3">Documents</h2>
          {!detail ? (
            <SkeletonCard lines={1} />
          ) : detail.documents.length === 0 ? (
            <div className="bg-white rounded-xl border border-legal-200 p-6 text-center text-sm text-legal-400">
              No documents in this case yet. Memo PDFs and uploads land here automatically.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-legal-200 divide-y divide-legal-100">
              {detail.documents.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/app/documents/${doc.id}`}
                  data-track="case-document-open"
                  className="flex items-center space-x-3 p-4 hover:bg-legal-50 transition-colors"
                >
                  <FileText className="h-4 w-4 text-legal-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-legal-800 truncate">{doc.original_filename}</p>
                    <p className="text-xs text-legal-400">
                      {(doc.file_size_bytes / 1024).toFixed(0)} KB ·{' '}
                      {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================= Tasks ================= */}
      {activeTab === 'tasks' && id && (
        <CaseTasksTab
          caseId={id}
          tasks={caseTasks}
          collabs={collabs}
          onChanged={() => {
            loadCaseTasks();
            invalidatePractice();
          }}
        />
      )}

      {/* ================= People ================= */}
      {activeTab === 'people' && id && (
        <div className="space-y-5">
          <CaseClientCard clientId={header.client_id} clientName={header.client_name} />
          <CollaboratorList caseId={id} readOnly={isSharedCase} />
        </div>
      )}

      {/* Assistant — scoped to this case (and, after "Log outcome", to a hearing).
          Docked below whichever tab is open. */}
      {isCase && (
        <div className="mt-6">
          <PracticeAssistant
            key={assistantScope?.hearingId ?? 'case'}
            matterId={id}
            hearingId={assistantScope?.hearingId}
            opener={assistantScope?.opener}
            autoExpand={!!assistantScope}
            onExecuted={() => {
              loadHearings();
              loadCaseTasks();
              refreshDetail();
            }}
          />
        </div>
      )}

      {/* Delete dialog — "case" wording, Archive offered as the primary alternative */}
      {showDeleteDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-legal-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in"
            onClick={() => setShowDeleteDialog(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Delete this case"
          >
            <Card
              className="w-full max-w-md rounded-b-none sm:rounded-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-semibold text-legal-900 mb-2">Delete this case?</h3>
              <p className="text-sm text-legal-500 mb-5">
                Research, drafts and documents are kept — they just leave this case file.
                {isCase && ' If you\'re done with the case, archiving keeps its diary searchable.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {isCase && (
                  <Button size="md" onClick={handleArchive} isLoading={isArchiving}>
                    <Archive className="h-4 w-4" /> Archive instead
                  </Button>
                )}
                <Button
                  variant={isCase ? 'secondary' : 'danger'}
                  size="md"
                  data-track="case-delete-confirm"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                  className={isCase ? 'text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300' : ''}
                >
                  <Trash2 className="h-4 w-4" /> Delete case
                </Button>
                <Button variant="ghost" size="md" onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
              </div>
            </Card>
          </div>,
          document.body
        )}

      {/* Archive-instead dialog (409 matter_has_hearings) — portaled to <body>:
          the page root is animated, which would otherwise become the containing
          block for this fixed overlay. */}
      {showArchiveDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-legal-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in"
            onClick={() => setShowArchiveDialog(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Archive this case instead"
          >
            <Card
              className="w-full max-w-md rounded-b-none sm:rounded-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-semibold text-legal-900 mb-2">
                This case has diary entries
              </h3>
              <p className="text-sm text-legal-500 mb-5">
                Its hearings are part of your practice record, so it can't be deleted.
                Archive it instead — it leaves the board and the cases list but stays searchable.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="md" onClick={handleArchive} isLoading={isArchiving}>
                  <Archive className="h-4 w-4" /> Archive case
                </Button>
                <Button variant="ghost" size="md" onClick={() => setShowArchiveDialog(false)}>
                  Cancel
                </Button>
              </div>
            </Card>
          </div>,
          document.body
        )}
    </div>
  );
}

/**
 * People tab: the client's contact card. Loads the full Client record when the
 * case links one (owner-only endpoint — shared viewers gracefully fall back to
 * the name, matching the ClientContactCard privacy rule).
 */
function CaseClientCard({
  clientId,
  clientName,
}: {
  clientId?: string | null;
  clientName?: string | null;
}) {
  const [client, setClient] = useState<Client | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let stale = false;
    clientsApi
      .get(clientId)
      .then(({ data }) => {
        if (!stale) setClient(data);
      })
      .catch(() => {
        if (!stale) setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [clientId]);

  if (!clientId && !clientName) {
    return (
      <div className="bg-white rounded-card border border-legal-200 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-legal-800 mb-1">
          <Contact className="h-4 w-4 text-legal-500" /> Client
        </p>
        <p className="text-xs text-legal-400">
          No client on this case yet — add one from Edit case.
        </p>
      </div>
    );
  }

  const name = client?.name || clientName || 'Client';
  const phone = client?.phone;
  const whatsapp = client?.whatsapp || client?.phone;

  return (
    <div className="bg-white rounded-card border border-legal-200 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-legal-800 mb-3">
        <Contact className="h-4 w-4 text-legal-500" /> Client
      </p>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {clientId && !failed ? (
            <Link
              to={`/app/clients/${clientId}`}
              data-track="case-people-client"
              className="text-sm font-medium text-legal-900 hover:text-accent-700 transition-colors break-words"
            >
              {name}
            </Link>
          ) : (
            <p className="text-sm font-medium text-legal-900 break-words">{name}</p>
          )}
          <div className="text-sm text-legal-600 mt-1 space-y-0.5">
            {phone && (
              <p className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-legal-400" /> {phone}
              </p>
            )}
            {client?.email && (
              <p className="flex items-center gap-1.5 break-all">
                <Mail className="h-3.5 w-3.5 text-legal-400" /> {client.email}
              </p>
            )}
          </div>
          {!clientId && (
            <p className="text-xs text-legal-400 mt-1.5">
              Name only — save them to Clients (Edit case) to get call and WhatsApp buttons.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {phone && (
            <Button variant="secondary" size="sm" href={`tel:${phone}`} data-track="call-client">
              <Phone className="h-3.5 w-3.5" /> Call
            </Button>
          )}
          {whatsapp && (
            <Button
              variant="secondary"
              size="sm"
              href={waLink(whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              data-track="call-client"
              className="text-[#25D366] hover:text-[#25D366]"
            >
              <WhatsAppIcon className="h-3.5 w-3.5" /> WhatsApp
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Tasks tab: full creation form (title / type / due date / assignee from this
 * case's collaborators) — replaces the old title-only quick-add.
 */
function CaseTasksTab({
  caseId,
  tasks,
  collabs,
  onChanged,
}: {
  caseId: string;
  tasks: Task[] | null;
  collabs: Collaborator[];
  onChanged: () => void;
}) {
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState('other');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const activeCollabs = collabs.filter((c) => c.status === 'active' && c.collaborator_user_id);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      await practiceApi.createTask({
        title: title.trim(),
        task_type: taskType,
        due_date: dueDate || undefined,
        matter_id: caseId,
        assignee_user_id: assigneeId || undefined,
      });
      setTitle('');
      setDueDate('');
      setAssigneeId('');
      onChanged();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not add the task.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2 className="font-semibold text-legal-900 mb-3">Tasks</h2>

      <form
        onSubmit={handleCreate}
        className="bg-white rounded-card border border-legal-200 p-4 mb-4 space-y-3"
      >
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          placeholder='e.g. "Prepare evidence affidavit"'
          className={INPUT_CLS}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            aria-label="Task type"
            className={INPUT_CLS}
          >
            {TASK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-label="Due date"
            className={INPUT_CLS}
          />
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            disabled={activeCollabs.length === 0}
            aria-label="Assignee"
            className={`${INPUT_CLS} disabled:bg-legal-50 disabled:text-legal-400`}
          >
            <option value="">
              {activeCollabs.length === 0
                ? 'No collaborators — invite from the People tab'
                : 'Assign to me'}
            </option>
            {activeCollabs.map((c) => (
              <option key={c.id} value={c.collaborator_user_id!}>
                {c.collaborator_name || c.invited_email}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button
          type="submit"
          size="md"
          data-track="case-task-create"
          isLoading={isSaving}
          disabled={!title.trim()}
        >
          <Plus className="h-4 w-4" /> Add task
        </Button>
      </form>

      {tasks === null ? (
        <SkeletonCard lines={1} />
      ) : tasks.length === 0 ? (
        <div className="bg-white rounded-card border border-legal-200 p-6 text-center text-sm text-legal-400">
          No tasks on this case yet.
        </div>
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One hearing on the timeline: date, purpose, status chip, outcome, provenance
 * badge, "Log outcome" via the assistant, plus a manual outcome form fallback.
 */
function HearingTimelineRow({
  hearing,
  onLogOutcome,
  onSaved,
}: {
  hearing: Hearing;
  onLogOutcome: (h: Hearing) => void;
  onSaved: () => void;
}) {
  const meta = hearingStatusMeta(hearing.status);
  const StatusIcon = meta.icon;
  const [showManual, setShowManual] = useState(false);
  const [status, setStatus] = useState<string>('held');
  const [outcome, setOutcome] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [nextPurpose, setNextPurpose] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const needsOutcome = hearing.status === 'scheduled' && !hearing.outcome;

  const saveManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      await practiceApi.updateHearing(hearing.id, {
        status,
        outcome: outcome.trim() || undefined,
        next_date: nextDate || undefined,
        next_purpose: nextPurpose.trim() || undefined,
      });
      setShowManual(false);
      setOutcome('');
      setNextDate('');
      setNextPurpose('');
      onSaved();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not save the outcome.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-legal-900">
            {dayLabel(hearing.hearing_date)}
            {hearing.purpose && <span className="text-legal-500 font-normal"> — {hearing.purpose}</span>}
          </p>
          {hearing.outcome && (
            <p className="text-sm text-legal-600 mt-1 break-words">{hearing.outcome}</p>
          )}
          {hearing.next_date && (
            <p className="text-xs text-legal-400 mt-1">Next date: {dayLabel(hearing.next_date)}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <Badge className={`${meta.bg} ${meta.text}`}>
              <StatusIcon className="h-3 w-3" />
              {meta.label}
            </Badge>
            {SOURCE_LABELS[hearing.source] && (
              <Badge tone="neutral">{SOURCE_LABELS[hearing.source]}</Badge>
            )}
            {!hearing.verified && <Badge tone="warning">auto — verify</Badge>}
          </div>
        </div>
        {needsOutcome && (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              data-track="log-outcome"
              onClick={() => onLogOutcome(hearing)}
            >
              <ClipboardEdit className="h-3.5 w-3.5" /> Log outcome
            </Button>
            <button
              type="button"
              onClick={() => setShowManual((v) => !v)}
              className="text-[11px] text-legal-400 hover:text-legal-600 transition-colors"
            >
              {showManual ? 'hide form' : 'or type it manually'}
            </button>
          </div>
        )}
      </div>

      {showManual && (
        <form onSubmit={saveManual} className="mt-3 space-y-2 animate-slide-up">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT_CLS}>
              <option value="held">Held</option>
              <option value="adjourned">Adjourned</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              type="date"
              value={nextDate}
              onChange={(e) => setNextDate(e.target.value)}
              aria-label="Next hearing date"
              className={INPUT_CLS}
            />
            <input
              value={nextPurpose}
              onChange={(e) => setNextPurpose(e.target.value)}
              maxLength={255}
              placeholder="Next purpose"
              className={INPUT_CLS}
            />
          </div>
          <textarea
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            maxLength={5000}
            rows={2}
            placeholder="What happened — orders passed, who appeared, what's next"
            className={`${INPUT_CLS} resize-none`}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" isLoading={isSaving}>
              Save outcome
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowManual(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
